
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Spinner from "Common/Globle/Spinner";
import { validateForm } from "utils/validation";
import { RoomBookingRoomRules } from "validation/ValidationRules";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import {
    getBookingById,
    updateRoomBooking,
    getAllPaymentMethods,
    getAllRoomTypes,
    getChargePercent,
    getRoomBookingTypes
} from "adminApi/RoomBookingApi/RoomBooking";
import EditRoomBookingForm from "./Components/EditRoomBookingForm";
import useFetchAllData from "../../Components/useFetchAllData";

// IMPORT YOUR FIXED JSON AS-IS
import fields from "../json/RoomBookingFields.json";

const { form_data } = fields;

// ---------------------------------------
// HELPERS
// ---------------------------------------

const normalizeDate = (str) => (str ? String(str).slice(0, 10) : "");

// EditRoomCheckIn.jsx

const { Reservation_Details, RoomFields, extraFields, Payment_Details, Advance_Details, Rent_Info } = form_data

// Build flat form fields as empty strings
const data = [...Reservation_Details, ...Payment_Details, ...Advance_Details]
const roomFields = [...RoomFields, ...extraFields, ...Rent_Info]

// Convert field name arrays to { fieldName: "" } objects
const flatInitialValues = Object.fromEntries(
    data.map((f) => [f.name, ""])
)

// Convert room field names to one empty room object
const emptyRoom = {
    ...Object.fromEntries(roomFields.map((f) => [f.name, ""])),
    capacity: ""
};

const initialValues = {
    ...flatInitialValues,
    rooms: [emptyRoom]
}

// ---------------------------------------
// MAIN COMPONENT
// ---------------------------------------

const EditRoomBooking = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [ChildrenMinAge, setChildrenMinAge] = useState(null);
    const [ChildrenMaxAge, setChildrenMaxAge] = useState(null);

    const [loadingFetch, setLoadingFetch] = useState(false);

    // Dropdown data
    const { data: bookingTypes = [] } = useFetchAllData({ apiCall: getRoomBookingTypes, IsDeleted: true });
    const { data: roomTypesOption = [] } = useFetchAllData({ apiCall: getAllRoomTypes, IsDeleted: true });
    const { data: paymentMethodTypesOption = [] } = useFetchAllData({ apiCall: getAllPaymentMethods, IsDeleted: true });
    const { data: taxConfig } = useFetchAllData({ apiCall: getChargePercent, IsDeleted: true });

    // KEEP JSON EXACT — JUST CLONE IT
    const clonedInitialValues = {
        ...initialValues,
        rooms: [{ ...initialValues.rooms[0] }],
    };

    const { values, setValues, errors, handleChange, setErrors } =
        useFormChange(clonedInitialValues, RoomBookingRoomRules);

    // ---------------------------------------
    // LOAD BOOKING DATA
    // ---------------------------------------
    useEffect(() => {
        if (!id) return;

        const fetchBooking = async () => {
            setLoadingFetch(true);

            try {
                const res = await getBookingById(id);
                const data = res?.data?.data || {};

                setChildrenMaxAge(data.children_max_age);
                setChildrenMinAge(data.children_min_age);

                setValues({
                    ...values,
                    ...data,
                    checkin_date: normalizeDate(data.checkin_date),
                    checkout_date: normalizeDate(data.checkout_date),
                    rooms: data.rooms?.length
                        ? data.rooms.map(room => ({
                            ...room,
                            extracheck_in_date: normalizeDate(room.extracheck_in_date),
                            extracheck_out_date: normalizeDate(room.extracheck_out_date),
                        }))
                        : [{ ...initialValues.rooms[0] }],
                });
            } catch (error) {
                console.error("Error fetching booking:", error);
            } finally {
                setLoadingFetch(false);
            }
        };

        fetchBooking();
    }, [id]);

    // ---------------------------------------
    // SUBMIT HANDLER
    // ---------------------------------------
    const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
        async (formValues) => {
            const formData = new FormData();

            // Send all keys except rooms
            Object.entries(formValues).forEach(([key, val]) => {
                if (key !== "rooms") formData.append(key, val ?? "");
            });

            formData.append("rooms", JSON.stringify(formValues.rooms));

            return updateRoomBooking({ id, data: formData });
        },
        (vals) => {
            const v = { ...vals };
            delete v.booking_status;

            /** REMOVE NON-EDITABLE FIELDS */
            delete v.customer_name;
            delete v.customer_phone;
            delete v.customer_email;
            delete v.customer_address;
            delete v.full_guest_name;

            /** VALIDATE */
            const validationErrors = validateForm(v, RoomBookingRoomRules);
            console.log(validationErrors);
            setErrors(validationErrors);
            return validationErrors;
        },
        () => navigate(-1),
        "Room Booking Updated Successfully"
    );

    return (
        <>
            {(loadingFetch || loadingSubmit) && <Spinner isSpinner={true} />}

            {!loadingFetch && (
                <EditRoomBookingForm
                    values={values}
                    setValues={setValues}
                    errors={errors}
                    handleChange={handleChange}
                    handleSubmit={(e) => handleSubmit(e, values)}
                    loading={loadingSubmit}
                    bookingTypesOptions={bookingTypes}
                    roomTypesOption={roomTypesOption}
                    paymentMethodTypesOption={paymentMethodTypesOption}
                    taxConfig={taxConfig}
                    initialValues={initialValues}
                    form_data={form_data}
                    ChildrenMinAge={ChildrenMinAge}
                    ChildrenMaxAge={ChildrenMaxAge}
                    emptyRoom={[...initialValues.rooms]}
                    roomFields={roomFields}
                />
            )}
        </>
    );
};

export default EditRoomBooking;